import { useTranslation } from "react-i18next";
import { formatPrice } from "@/lib/format-utils";
import { ClientBalanceEntry, Payment } from "@turizi/shared";
import {
    IconScale,
    IconShoppingCart,
    IconBuildingStore,
    IconArrowDownLeft,
    IconArrowUpRight,
    IconLink,
    IconLinkOff
} from "@tabler/icons-react";

interface BalanceDetailsProps {
    selectedClient: ClientBalanceEntry | null;
    payments?: Payment[];
}

export default function BalanceDetails({ selectedClient, payments }: BalanceDetailsProps) {
    const { t } = useTranslation();

    if (!selectedClient) return null;

    const entries = Object.entries(selectedClient.balances || {});
    if (entries.length === 0) return null;

    const computeUnassignedForCurrency = (currency: string, bal: any) => {
        if (typeof bal?.unassigned_paid === 'number') {
            return bal.unassigned_paid;
        }

        const paymentList = (payments && payments.length > 0)
            ? payments
            : (selectedClient.details?.payments || []);

        return paymentList
            .filter((p: any) => {
                if ((p.currency || 'ARS') !== currency) return false;
                if (p.status === 'cancelled') return false;
                // Only count incoming payments from client
                if (selectedClient.id && p.from_business_id && p.from_business_id !== selectedClient.id) {
                    return false;
                }
                if (selectedClient.id && p.from_person_id && p.from_person_id !== selectedClient.id) {
                    return false;
                }
                return true;
            })
            .reduce((sum: number, p: any) => {
                const amt = Number(p.amount || 0);
                const ref = Number(p.refunded_amount || 0);
                const net = Math.max(0, amt - ref);

                const allocRes = (p.reservations || []).reduce((acc: number, r: any) => {
                    return acc + Number(r.payment_reservation?.amount ?? r.PaymentReservation?.amount ?? 0);
                }, 0);
                const allocBk = (p.service_bookings || []).reduce((acc: number, b: any) => {
                    return acc + Number(b.payment_service_booking?.amount ?? b.PaymentServiceBooking?.amount ?? 0);
                }, 0);
                const totalAllocated = allocRes + allocBk;
                const unalloc = Math.max(0, net - totalAllocated);
                return sum + unalloc;
            }, 0);
    };

    return (
        <div className="flex flex-wrap gap-1">
            {entries.map(([currency, bal]) => {
                const isWeOwe = bal.total > 0;
                const isTheyOwe = bal.total < 0;
                const amountStr = formatPrice(Math.abs(bal.total));

                const isPerson = selectedClient.type === 'person';
                const soldAmount = isPerson ? (bal.consumed || 0) : (bal.sold || 0);

                const unassignedAmount = computeUnassignedForCurrency(currency, bal);
                const assignedAmount = Math.max(0, (bal.paid || 0) - unassignedAmount);

                return (
                    <div
                        key={currency}
                        className="bg-white border border-slate-200/90 rounded-xl overflow-hidden shadow-2xs w-[500px]"
                    >
                        {/* Currency Header Bar */}
                        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 bg-slate-50/80 border-b border-slate-200/80">
                            <div className="flex items-center gap-2">
                                <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                                    {t('account_statement')}
                                </span>
                            </div>

                            {/* Net Balance Badge */}
                            <span className="px-2.5 py-1 text-xs  font-semibold text-black bg-white border border-slate-200 rounded-md shadow-2xs">
                                {currency}
                            </span>
                        </div>

                        {/* Vertical Stack List */}
                        <div className="divide-y divide-slate-100 text-xs">
                            {/* 1. Purchases */}
                            <div className="flex items-center justify-between gap-20 px-4 py-2.5 hover:bg-slate-50/40 transition-colors">
                                <div className="flex items-center gap-2.5">
                                    <div className="p-1.5 bg-green-50 text-green-600 rounded-md shrink-0">
                                        <IconShoppingCart size={15} stroke={2} />
                                    </div>
                                    <div>
                                        <div className="font-semibold text-slate-800">{t('purchases')}</div>
                                        <div className="text-[11px] text-slate-400 font-light">
                                            {t('by')} {selectedClient.name}
                                        </div>
                                    </div>
                                </div>
                                <div className="font-semibold text-slate-800 text-sm">
                                    {formatPrice(soldAmount)}{' '}
                                    <span className="text-[10px]  font-bold text-slate-500 bg-slate-100 border border-slate-200 px-1 py-0.5 rounded ml-0.5">
                                        {currency}
                                    </span>
                                </div>
                            </div>

                            {/* 2. Selected as Provider */}
                            <div className="flex items-center justify-between px-4 py-2.5 hover:bg-slate-50/40 transition-colors">
                                <div className="flex items-center gap-2.5">
                                    <div className="p-1.5 bg-amber-50 text-amber-600 rounded-md shrink-0">
                                        <IconBuildingStore size={15} stroke={2} />
                                    </div>
                                    <div>
                                        <div className="font-semibold text-slate-800">{t('selected_as_provider')}</div>
                                    </div>
                                </div>
                                <div className="font-semibold text-slate-800 text-sm">
                                    {formatPrice(bal.provided || 0)}{' '}
                                    <span className="text-[10px]  font-bold text-slate-500 bg-slate-100 border border-slate-200 px-1 py-0.5 rounded ml-0.5">
                                        {currency}
                                    </span>
                                </div>
                            </div>

                            {/* 3. Money Received */}
                            <div className="flex items-center justify-between px-4 py-2.5 hover:bg-slate-50/40 transition-colors">
                                <div className="flex items-center gap-2.5">
                                    <div className="p-1.5 bg-emerald-50 text-emerald-600 rounded-md shrink-0">
                                        <IconArrowDownLeft size={15} stroke={2} />
                                    </div>
                                    <div>
                                        <div className="font-semibold text-slate-800">{t('money_received')}</div>
                                        <div className="text-[11px] text-slate-400 font-light">
                                            {t('by')} {selectedClient.name}
                                        </div>
                                    </div>
                                </div>
                                <div className="font-semibold text-emerald-700 text-sm">
                                    {formatPrice(bal.paid || 0)}{' '}
                                    <span className="text-[10px]  font-bold text-emerald-700 bg-emerald-50 border border-emerald-200/60 px-1 py-0.5 rounded ml-0.5">
                                        {currency}
                                    </span>
                                </div>
                            </div>

                            {/* Sub-rows for Money Received breakdown */}
                            {((bal.paid || 0) > 0 || unassignedAmount > 0) && (
                                <>
                                    {/* 3a. Assigned to Reservations */}
                                    <div className="flex items-center justify-between pl-10 pr-4 py-2 bg-emerald-50/20 text-xs">
                                        <div className="flex items-center gap-2">
                                            <div className="p-1 rounded bg-emerald-100/80 text-emerald-700 shrink-0">
                                                <IconLink size={13} stroke={2} />
                                            </div>
                                            <span className="font-medium text-slate-700">
                                                {t('assigned_to_reservations')}
                                            </span>
                                        </div>
                                        <div className="font-semibold text-emerald-700 text-xs">
                                            ${formatPrice(assignedAmount)}{' '}
                                            <span className="text-[9px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200/60 px-1 py-0.5 rounded ml-0.5">
                                                {currency}
                                            </span>
                                        </div>
                                    </div>

                                    {/* 3b. Unassigned Money */}
                                    <div className="flex items-center justify-between pl-10 pr-4 py-2 bg-amber-50/20 text-xs">
                                        <div className="flex items-center gap-2">
                                            <div className="p-1 rounded bg-amber-100/80 text-amber-700 shrink-0">
                                                <IconLinkOff size={13} stroke={2} />
                                            </div>
                                            <span className="font-medium text-slate-700">
                                                {t('unallocated_balance')}
                                            </span>
                                        </div>
                                        <div className={`font-semibold text-xs ${unassignedAmount > 0 ? 'text-amber-700' : 'text-slate-500'}`}>
                                            ${formatPrice(unassignedAmount)}{' '}
                                            <span className={`text-[9px] font-bold px-1 py-0.5 rounded ml-0.5 ${unassignedAmount > 0 ? 'text-amber-700 bg-amber-50 border border-amber-200/60' : 'text-slate-500 bg-slate-100 border border-slate-200'}`}>
                                                {currency}
                                            </span>
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* 4. Money Sent */}
                            <div className="flex items-center justify-between px-4 py-2.5 hover:bg-slate-50/40 transition-colors">
                                <div className="flex items-center gap-2.5">
                                    <div className="p-1.5 bg-orange-50 text-orange-600 rounded-md shrink-0">
                                        <IconArrowUpRight size={15} stroke={2} />
                                    </div>
                                    <div>
                                        <div className="font-semibold text-slate-800">{t('money_sent')}</div>
                                        <div className="text-[11px] text-slate-400 font-light">
                                            {t('by')} {selectedClient.my_business_name}
                                        </div>
                                    </div>
                                </div>
                                <div className="font-semibold text-orange-700 text-sm">
                                    {formatPrice(bal.payout || 0)}{' '}
                                    <span className="text-[10px]  font-bold text-orange-700 bg-orange-50 border border-orange-200/60 px-1 py-0.5 rounded ml-0.5">
                                        {currency}
                                    </span>
                                </div>
                            </div>

                            {/* 5. Net Balance Row */}
                            <div
                                className={`flex items-center justify-between px-4 py-3 font-bold ${isTheyOwe
                                    ? 'bg-orange-50/50'
                                    : isWeOwe
                                        ? 'bg-emerald-50/50'
                                        : 'bg-slate-50/60'
                                    }`}
                            >
                                <div className="flex items-center gap-2.5">
                                    <div
                                        className={`p-1.5 rounded-md shrink-0 ${isTheyOwe
                                            ? 'bg-orange-100 text-orange-700'
                                            : isWeOwe
                                                ? 'bg-emerald-100 text-emerald-700'
                                                : 'bg-slate-200 text-slate-700'
                                            }`}
                                    >
                                        <IconScale size={16} stroke={2} />
                                    </div>
                                    <div>
                                        <div className="text-xs uppercase tracking-wider text-slate-700">
                                            {t('balance')}
                                        </div>
                                        {isTheyOwe && (
                                            <div className="text-[10px] font-semibold text-orange-700">
                                                {t('owes')}
                                            </div>
                                        )}
                                        {isWeOwe && (
                                            <div className="text-[10px] font-semibold text-emerald-700">
                                                {t('client_surplus_money')}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div
                                    className={`text-base font-extrabold ${isTheyOwe
                                        ? 'text-orange-700'
                                        : isWeOwe
                                            ? 'text-emerald-700'
                                            : 'text-slate-900'
                                        }`}
                                >
                                    ${amountStr}{' '}
                                    <span className="text-[11px]  font-bold opacity-80">{currency}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
